import { useState } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QualityScore } from "@/components/csv-analysis/analysis-details/QualityScore";
import { FileText, Trash2, AlertCircle, CheckCircle2, HelpCircle } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { QualityAcknowledgement } from "./controls/QualityAcknowledgement";
import { ActionButtons } from "./buttons/ActionButtons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AnalysisCardProps {
  analysis: {
    id: string;
    file_name: string;
    file_path: string;
    analysis_status: string;
    analysis_result: any;
    version_type: string;
    version_notes?: string;
  };
  onDelete: () => void;
  onGenerateSchema: (id: string, tableName: string) => void;
  onImportData: (id: string, tableName: string, filePath: string) => void;
  isProcessing: boolean;
  onAnalysisComplete: () => void;
}

export const AnalysisCard = ({
  analysis,
  onDelete,
  onGenerateSchema,
  onImportData,
  isProcessing,
  onAnalysisComplete,
}: AnalysisCardProps) => {
  const [tableName, setTableName] = useState('temp_');
  const [isTemporary, setIsTemporary] = useState(true);
  const [acknowledgeIssues, setAcknowledgeIssues] = useState(false);
  const [schemaGenerated, setSchemaGenerated] = useState(false);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'analyzing':
        return { icon: FileText, variant: 'secondary', label: 'Analyzing' };
      case 'completed':
        return { icon: CheckCircle2, variant: 'default', label: 'Completed' };
      case 'failed':
        return { icon: AlertCircle, variant: 'destructive', label: 'Failed' };
      default:
        return { icon: FileText, variant: 'secondary', label: status };
    }
  };

  const handleTableNameChange = (value: string) => {
    let newName = value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (isTemporary && !newName.startsWith('temp_')) {
      newName = `temp_${newName}`;
    } else if (!isTemporary && newName.startsWith('temp_')) {
      newName = newName.replace(/^temp_/, '');
    }
    setTableName(newName);
  };

  const handleGenerateSchema = async () => {
    await onGenerateSchema(analysis.id, tableName);
    setSchemaGenerated(true);
  };

  const status = getStatusConfig(analysis.analysis_status);
  const StatusIcon = status.icon;
  const isAnalyzing = analysis.analysis_status === 'analyzing';
  const score = analysis.analysis_result?.qualityScore || 0;
  const hasQualityIssues = analysis.analysis_result?.qualityIssues?.length > 0;
  const hasTimeSeriesIssues = analysis.analysis_result?.timeSeriesAnalysis?.isTimeSeries;

  const handleImport = () => {
    onImportData(analysis.id, tableName, analysis.file_path);
  };

  const actionsEnabled = hasQualityIssues;
  const importEnabled = hasQualityIssues ? acknowledgeIssues : true;
  const schemaEnabled = tableName.length >= 5;

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex flex-col space-y-1.5">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold leading-none">{analysis.file_name}</h3>
            <Badge variant={status.variant as any} className="h-6">
              {isAnalyzing ? (
                <Spinner size="xs" className="mr-1" ariaLabel="Analyzing" />
              ) : (
                <StatusIcon className="h-3 w-3 mr-1" />
              )}
              {status.label}
            </Badge>
          </div>
          {analysis.version_type !== 'original' && (
            <Badge variant="outline" className="w-fit">
              Cleaned Version
            </Badge>
          )}
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onDelete}
          className="hover:bg-destructive hover:text-destructive-foreground transition-colors"
        >
          <Trash2 className="h-5 w-5" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        <QualityScore
          score={score}
          qualityIssues={analysis.analysis_result?.qualityIssues}
          analysisId={analysis.id}
          onAnalysisComplete={onAnalysisComplete}
          hasQualityIssues={hasQualityIssues}
          timeSeriesAnalysis={analysis.analysis_result?.timeSeriesAnalysis}
          hasTimeSeriesIssues={hasTimeSeriesIssues}
        />

        <div className="space-y-4">
          {hasQualityIssues && (
            <QualityAcknowledgement
              acknowledgeIssues={acknowledgeIssues}
              onAcknowledgeChange={setAcknowledgeIssues}
              score={score}
            />
          )}

          <ActionButtons
            onImportData={handleImport}
            onGenerateSchema={handleGenerateSchema}
            isProcessing={isProcessing}
            importEnabled={importEnabled}
            actionsEnabled={actionsEnabled}
            hasQualityIssues={hasQualityIssues}
            schemaEnabled={schemaEnabled}
            schemaGenerated={schemaGenerated}
          />

          <div className="mt-6 space-y-4 border-t pt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="tableName" className="text-sm font-medium">
                  Table Name {!schemaEnabled && (
                    <span className="text-xs text-muted-foreground ml-2">
                      (minimum 5 characters required)
                    </span>
                  )}
                </Label>
                <Input
                  id="tableName"
                  value={tableName}
                  onChange={(e) => handleTableNameChange(e.target.value)}
                  className="max-w-md"
                  placeholder="Enter table name"
                />
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <Checkbox
                  id="temporary"
                  checked={isTemporary}
                  onCheckedChange={(checked) => {
                    setIsTemporary(checked === true);
                    handleTableNameChange(tableName);
                  }}
                />
                <Label htmlFor="temporary" className="text-sm text-gray-600">
                  Create as temporary table
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Temporary tables are prefixed with 'temp_' and are useful for data preparation 
                        before merging into existing tables
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
